import { Button, Sidebar, SidebarContent, SidebarFooter, Tabs, TabsContent, useSidebar } from '@ibsys/kit';
import { CSSProperties, FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './AppSideBar.module.css';
import { useIsSidebarCollapsed } from '@/shared/hooks';
import { cn } from '@/shared/lib';
import { RouteConfig } from '@/shared/lib/ts-router';
import { Icon, IconButton, useMainLayout } from '@/shared/ui';
import { AppSidebaItems } from './AppSidebaItems';
import { CollapsedAiActions } from './CollapsedAiActions';
import { PanelsBar } from '@/widgets/MainLayout/ui/PanelsBar';
import { CopilotPanel } from '@/widgets/Copilot';
import { SidebarPanels } from '@/entities/settings';
import { useHasPermission, useMyProfile } from '@/entities/profile';
import { AppSidebarHeader } from './AppSiedbarHeader';
import { SIDEBAR_PROVIDER_ID } from '@/widgets/MainLayout/model/constants';
import { useHasOtherSidebarPanels } from '@/widgets/MainLayout/model/hooks/useHasOtherSidebarPanels';
import { useLocation } from 'react-router-dom';

/** Минимальная и максимальная ширина панели copilot */
const COPILOT_MIN_WIDTH = 320;
const COPILOT_MAX_WIDTH = 900;

type Props = {
  menuItems: RouteConfig[];
  style?: CSSProperties;
};

export const AppSidebar: FC<Props> = (props) => {
  const { menuItems, style } = props;

  const { t } = useTranslation(['common']);
  const { toggleSidebar, isMobile, openMobile, setOpenMobile, setOpen } = useSidebar();
  const { sidebar } = useMainLayout();
  const { setActivePanel } = sidebar;
  const { pathname } = useLocation();
  const canUseAiAgent = useHasPermission('USE_AI_AGENT');
  const hasOtherPanels = useHasOtherSidebarPanels();
  const { isSuccess: isProfileLoaded } = useMyProfile();

  useEffect(() => {
    if (openMobile && isMobile && sidebar.activePanel === 'navigation') {
      setOpenMobile(false);
    }
  }, [pathname, isMobile]);

  // В свёрнутом состоянии показывается только панель навигации: колонка PanelsBar не рендерится,
  // а её кнопки ИИ-агента переезжают наверх этой единственной панели (см. CollapsedAiActions).
  const isCollapsedNav = useIsSidebarCollapsed();

  // Колонке без других панелей нечего переключать; на узком экране в ней остаётся переключатель меню.
  const isPanelsBarVisible = !isCollapsedNav && (hasOtherPanels || isMobile);

  // Панель ИИ-агента не может быть свёрнута до иконок — подстраховка на случай ранее сохранённого состояния.
  // Без права панель сменяется навигацией, и раскрывать ради неё меню незачем.
  useEffect(() => {
    if (isCollapsedNav && canUseAiAgent && sidebar.activePanel === 'copilot') {
      setOpen(true);
    }
  }, [isCollapsedNav, canUseAiAgent, sidebar.activePanel, setOpen]);

  // Сохранённая настройка переживает утрату права, а показывать по ней недоступную панель нельзя.
  // Смена ждёт профиля: до его загрузки прав нет ни у кого, и пользователь с правом терял бы
  // открытую панель при каждом входе.
  useEffect(() => {
    if (isProfileLoaded && !canUseAiAgent && sidebar.activePanel === 'copilot') {
      setActivePanel('navigation');
    }
  }, [isProfileLoaded, canUseAiAgent, sidebar.activePanel, setActivePanel]);

  const [, setDisplayWidth] = useState<number | null>(null);

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (!isMobile) {
        const startX = e.clientX;
        const startWidth = sidebar.copilotWidth;

        const handleMouseMove = (ev: MouseEvent) => {
          let delta = ev.clientX - startX;
          const newWidth = Math.max(COPILOT_MIN_WIDTH, Math.min(COPILOT_MAX_WIDTH, startWidth + delta));
          setDisplayWidth(newWidth);
          requestAnimationFrame(() => {
            document.getElementById(SIDEBAR_PROVIDER_ID)?.style.setProperty('--sidebar-width-temp', `${newWidth}px`);
          });
        };

        const handleMouseUp = () => {
          setDisplayWidth((width) => {
            width && sidebar.setCopilotWidth(width);
            return null;
          });
          document.getElementById(SIDEBAR_PROVIDER_ID)?.style.removeProperty('--sidebar-width-temp');
          window.removeEventListener('mousemove', handleMouseMove);
          window.removeEventListener('mouseup', handleMouseUp);
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
      }
    },
    [sidebar],
  );

  return (
    <Sidebar
      classNames={{
        wrapper: '',
        inner: 'gap-0',
      }}
      className={cn(styles.root, !isMobile && 'z-20')}
      style={style}
      collapsible={'icon'}
      variant={'sidebar'}
    >
      <Tabs
        className={'flex-1 flex flex-row gap-0 h-full min-h-0'}
        value={sidebar.activePanel}
        orientation={'vertical'}
        onValueChange={(v) => sidebar.setActivePanel(v as SidebarPanels)}
      >
        {isPanelsBarVisible && <PanelsBar className={'px-2 h-full'} />}
        <SidebarContent className={cn('transition-[translate] duration-200')}>
          <TabsContent value={'navigation'} className={'flex-1 flex flex-col'}>
            <AppSidebarHeader className={'sticky top-0 z-1'} />
            {isCollapsedNav && <CollapsedAiActions />}
            <AppSidebaItems menuItems={menuItems} className={'px-2'} />
          </TabsContent>
          <TabsContent value={'copilot'} className={'flex-1 flex flex-col h-full relative'}>
            <AppSidebarHeader className={'sticky top-0 z-1'} />
            <CopilotPanel />
            <div
              role={'separator'}
              tabIndex={-1}
              className={'absolute right-0 top-0 h-full w-1 cursor-col-resize z-30 hover:bg-border transition-colors'}
              onMouseDown={handleResizeStart}
            />
          </TabsContent>
        </SidebarContent>
      </Tabs>
      {/* Пока активна панель ИИ-агента, сворачивать панель контента нельзя — она уже раскрыта принудительно. */}
      {!isMobile && sidebar.activePanel !== 'copilot' && (
        <SidebarFooter className={'border-t border-sidebar-border p-1!'}>
          {isCollapsedNav ? (
            <IconButton
              icon={<Icon type={'collapse-right'} />}
              label={t('common:expandMenu')}
              onClick={toggleSidebar}
              size={'icon'}
              className={'hover:bg-sidebar-accent! hover:text-sidebar-accent-foreground!'}
            />
          ) : (
            <Button
              icon={<Icon type={'collapse-left'} />}
              onClick={toggleSidebar}
              variant={'ghost'}
              className={'justify-start! hover:bg-sidebar-accent! hover:text-sidebar-accent-foreground!'}
            >
              {t('common:collapseMenu')}
            </Button>
          )}
        </SidebarFooter>
      )}
    </Sidebar>
  );
};
